// SideBar.tsx
import React, { useCallback } from 'react';
import { AppRoutes } from '../../Constants';
import {
    useNavigate,
    useLocation,
    matchPath,
    useParams,
} from 'react-router-dom';
import { ReactComponent as GridViewIcon } from '../../assets/icons/New Brand Icons/grid-view.svg';
import { ReactComponent as DatabaseIcon } from '../../assets/icons/New Brand Icons/database.svg';
import { ReactComponent as ArchiveIcon } from '../../assets/icons/New Brand Icons/archive.svg';
import { ReactComponent as FolderDocumentsIcon } from '../../assets/icons/New Brand Icons/folder-documents.svg';
import { ReactComponent as DataAnalysisIcon } from '../../assets/icons/New Brand Icons/data-analysis.svg';
import { ReactComponent as FileIcon } from '../../assets/icons/New Brand Icons/file.svg';
import { ReactComponent as ChevronBackIcon } from '../../assets/icons/New Brand Icons/chevron-back.svg';
import { ReactComponent as ChevronForwardIcon } from '../../assets/icons/New Brand Icons/chevron-forward.svg';
import { useTenant } from '../../context/TenantContext';
import './SideBar.scss';

type SideBarProps = {
    onToggle?: (collapsed: boolean) => void;
};

interface NavItem {
    name: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    route: string;
}

function SideBar({ onToggle }: SideBarProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeTenantId } = useParams();
    const { selectedTenant } = useTenant();
    const parsedRouteTenantId = Number(routeTenantId);
    const tenantId =
        Number.isInteger(parsedRouteTenantId) && parsedRouteTenantId > 0
            ? parsedRouteTenantId
            : selectedTenant?.tenantId;

    const navItems: NavItem[] = [
        {
            name: 'Fixed Asset Data',
            icon: GridViewIcon,
            route: AppRoutes.FixedAsset,
        },
        {
            name: 'Project Data',
            icon: DatabaseIcon,
            route: AppRoutes.ProjectData,
        },
        { name: 'Tax Pools', icon: ArchiveIcon, route: AppRoutes.TaxPools },
        { name: 'Rules', icon: FolderDocumentsIcon, route: AppRoutes.Rules },
        {
            name: 'Reporting',
            icon: DataAnalysisIcon,
            route: AppRoutes.Reporting,
        },
        { name: 'Logs', icon: FileIcon, route: AppRoutes.Log },
    ];

    const [collapsed, setCollapsed] = React.useState(true);

    const isActive = useCallback(
        (route: string) =>
            !!matchPath({ path: route, end: false }, location.pathname),
        [location.pathname]
    );

    const onCollapseToggle = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onToggle?.(newCollapsed);
    };

    const getNavigationRoute = (route: string) => {
        if (
            tenantId !== undefined &&
            (route === AppRoutes.FixedAsset || route === AppRoutes.ProjectData)
        ) {
            return route.replace(':id', String(tenantId));
        }

        return route;
    };

    return (
        <nav
            className={`custom-sidebar ${collapsed ? 'custom-sidebar--collapsed' : ''}`}
        >
            <ul className="custom-sidebar__list">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.route);
                    return (
                        <li key={item.route}>
                            <button
                                className={`custom-sidebar__item ${active ? 'custom-sidebar__item--active' : ''}`}
                                onClick={() =>
                                    navigate(getNavigationRoute(item.route))
                                }
                                title={collapsed ? item.name : undefined}
                                type="button"
                            >
                                <Icon className="custom-sidebar__icon" />
                                {!collapsed && (
                                    <span className="custom-sidebar__label">
                                        {item.name}
                                    </span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
            <button
                className="custom-sidebar__collapse"
                onClick={onCollapseToggle}
                type="button"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
                {collapsed ? (
                    <ChevronForwardIcon className="custom-sidebar__icon" />
                ) : (
                    <ChevronBackIcon className="custom-sidebar__icon" />
                )}
            </button>
        </nav>
    );
}

export default SideBar;
